import { isCatsaExtrasApplicable, isInoperableCategory } from '@/Pages/WorkOrders/Modals/catsaExtras.js';
import { computed, toValue } from 'vue';

const OPEN_STATUSES = ['draft', 'to_do', 'in_progress'];

// Visibility rule for the inoperable-asset reminder banner. `workOrderStatus` is
// optional — pass it when an existing WO is loaded; omit it for the create flow
// (a new WO is always open at creation, so the status check is skipped).
//
// The banner is a persistent reminder for the lifetime of the WO: it shows whenever the
// current category is inoperable AND either:
//   (a) the stored category was not yet inoperable (user is transitioning), or
//   (b) the asset is ALREADY inoperable (auto-created WOs, legacy unacknowledged WOs).
//
// In both cases the banner stays visible after the checkbox is ticked — inoperabilityAckMissing
// gates Save, not visibility. The banner only hides once the WO is closed or the asset is
// restored to operable.
//
// Case (b) intentionally excludes WOs whose stored category is inoperable on a still-
// operable asset — that stale-category state won't cause a server-side flip on save, so
// no reminder is required (mirrors UpdateWorkOrderAction::syncAssetOperabilityWithCaseCategory).
//
// `previousCaseCategory` is the immutable stored value (null on create).
export function useInoperableReminder({
    reminderKey,
    workOrderType,
    workOrderStatus = () => null,
    caseCategory = () => null,
    previousCaseCategory = () => null,
    assetIsInoperable = () => false,
    catsaExtras = null,
}) {
    const key = computed(() => toValue(reminderKey) || null);

    const visible = computed(() => {
        if (!key.value) return false;

        const type = toValue(workOrderType);
        if (type !== 'corrective_maintenance') return false;

        const catsaConfig = toValue(catsaExtras);
        if (!isCatsaExtrasApplicable(type, catsaConfig)) return false;

        const nowInoperable = isInoperableCategory(toValue(caseCategory), catsaConfig);
        if (!nowInoperable) return false;

        const wasInoperable = isInoperableCategory(toValue(previousCaseCategory), catsaConfig);
        const assetInoperable = toValue(assetIsInoperable);
        // Hide when already was inoperable AND the asset is still operable (stale category,
        // no flip will occur on save).
        if (wasInoperable && !assetInoperable) return false;

        const status = toValue(workOrderStatus);
        if (status !== null && !OPEN_STATUSES.includes(status)) return false;

        return true;
    });

    return { reminderKey: key, visible };
}
